/* eslint-disable no-param-reassign */

import { createSlice } from '@reduxjs/toolkit';
import { updateStorePaymentAddress } from 'src/helpers/utils';
import { PRO_TIER_TYPES } from 'src/components/careerProPitch/tiers';
import { StoreInitialState } from '../types/store';
import { RootState } from '..';
import {
  abandonSubscriptionRestore,
  cancelSubscription,
  claimOrderVoucher,
  confirmSubscriptionCancel,
  getCartDetails,
  getMySubscription,
  getOrderHistory,
  getSubscriptionInvoices,
  getPaymentAddress,
  getStoreItems,
  updatePaymentAddress,
} from '../actions/store';

const initialState: StoreInitialState = {
  shopItems: [],
  rewardItems: [],
  cartDetails: {},
  orderHistory: [],
  checkoutDetails: undefined,
  isCartDetailsLoading: false,
  isCartTotalLoading: false,
  isStoreItemsLoading: false,
  isCheckoutLoading: false,
  paymentStatus: undefined,
  isOrderHistoryLoading: false,
  view: 'Abekus Shop',
  isMenuOpen: false,
  isMySubscriptionLoading: false,
  isCancellingSubscription: false,
  isSubscriptionInvoicesLoading: false,
};

const storeSlice = createSlice({
  name: 'store',
  initialState,
  reducers: {
    setStoreItems: (state, action) => {
      state.shopItems = action.payload;
    },
    setPaymentStatus: (state, action) => {
      state.paymentStatus = action.payload;
    },
    toggleView: (state) => {
      state.view =
        state.view === 'Abekus Shop' ? 'Abekus Rewards' : 'Abekus Shop';
    },
    toggleMenu: (state) => {
      state.isMenuOpen = !state.isMenuOpen;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMySubscription.pending, (state) => {
        state.isMySubscriptionLoading = true;
      })
      .addCase(getMySubscription.fulfilled, (state, action) => {
        state.isMySubscriptionLoading = false;
        state.mySubscription = action.payload.subscription;
        state.subscriptionCancelNotice = action.payload.cancelNotice;
      })
      .addCase(getMySubscription.rejected, (state) => {
        state.isMySubscriptionLoading = false;
        // Left as-is, not cleared: a transient failure must not turn an
        // already-loaded plan panel into "you have no subscription".
      })
      .addCase(cancelSubscription.pending, (state) => {
        state.isCancellingSubscription = true;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.isCancellingSubscription = false;
        state.mySubscription = action.payload;
      })
      .addCase(cancelSubscription.rejected, (state) => {
        state.isCancellingSubscription = false;
      })
      .addCase(confirmSubscriptionCancel.fulfilled, (state, action) => {
        state.subscriptionCancelNotice = action.payload;
      })
      .addCase(abandonSubscriptionRestore.fulfilled, (state, action) => {
        state.subscriptionCancelNotice = action.payload;
      })
      .addCase(getSubscriptionInvoices.pending, (state) => {
        state.isSubscriptionInvoicesLoading = true;
      })
      .addCase(getSubscriptionInvoices.fulfilled, (state, action) => {
        state.isSubscriptionInvoicesLoading = false;
        state.subscriptionInvoices = action.payload;
      })
      .addCase(getSubscriptionInvoices.rejected, (state) => {
        state.isSubscriptionInvoicesLoading = false;
        state.subscriptionInvoices = null;
      });

    builder
      .addCase(getStoreItems.pending, (state) => {
        state.isStoreItemsLoading = true;
      })
      .addCase(getStoreItems.fulfilled, (state, action) => {
        state.isStoreItemsLoading = false;
        state.proUnlocks = action.payload.proUnlocks;
        state.rewardItems = action.payload.items
          .filter((item) => item.is_reward)
          .sort(
            (a, b) =>
              (b.gross_price_in_gems_after_disc ?? 0) -
              (a.gross_price_in_gems_after_disc ?? 0),
          );
        state.shopItems = action.payload.items.filter((item) =>
          // Cash buys two things on web: credits and a Pro tier. The legacy unlimited
          // `premium_profile` is excluded so it is no longer sold (existing
          // buyers unaffected), and `applicant_spotlight` because web now buys
          // Spotlight from the credit balance instead. That item is still
          // returned by the API and still checked out by the mobile app through
          // the cart — this filter is what hides it from web, so nothing has to
          // be deleted server-side until the app stops selling it too.
          ['credits', ...PRO_TIER_TYPES].includes(item.item_type!),
        );
      })
      .addCase(getStoreItems.rejected, (state) => {
        state.isStoreItemsLoading = false;
      });

    builder
      .addCase(getCartDetails.pending, (state) => {
        state.isCartDetailsLoading = true;
        state.isCartTotalLoading = true;
      })
      .addCase(getCartDetails.fulfilled, (state, action) => {
        state.isCartDetailsLoading = false;
        state.isCartTotalLoading = false;
        state.cartDetails = action.payload;
      })
      .addCase(getCartDetails.rejected, (state) => {
        state.isCartTotalLoading = false;
        state.isCartDetailsLoading = false;
      });

    builder
      .addCase(getPaymentAddress.pending, (state) => {
        state.isCheckoutLoading = true;
      })
      .addCase(getPaymentAddress.fulfilled, (state, action) => {
        state.isCheckoutLoading = false;
        if (action.payload) {
          state.checkoutDetails = updateStorePaymentAddress(action.payload);
        }
      })
      .addCase(getPaymentAddress.rejected, (state) => {
        state.isCheckoutLoading = false;
      });

    builder
      .addCase(updatePaymentAddress.pending, (state) => {
        state.isCheckoutLoading = true;
      })
      .addCase(updatePaymentAddress.fulfilled, (state, action) => {
        state.isCheckoutLoading = false;
        state.checkoutDetails = updateStorePaymentAddress(action.payload);
      })
      .addCase(updatePaymentAddress.rejected, (state) => {
        state.isCheckoutLoading = false;
      });

    builder
      .addCase(getOrderHistory.pending, (state) => {
        state.isOrderHistoryLoading = true;
      })
      .addCase(getOrderHistory.fulfilled, (state, action) => {
        state.isOrderHistoryLoading = false;
        state.orderHistory = action.payload;
      })
      .addCase(getOrderHistory.rejected, (state) => {
        state.isOrderHistoryLoading = false;
      });

    builder.addCase(claimOrderVoucher.fulfilled, (state, action) => {
      const transactionId = action.payload;
      const order = state.orderHistory.find((o) => o.id === transactionId);
      if (order && (order as any).voucher) {
        (order as any).voucher.is_claimed = true;
      }
    });
  },
});

export const { setStoreItems, setPaymentStatus, toggleView, toggleMenu } =
  storeSlice.actions;

export const storeSelector = (state: RootState) => state.store;

export default storeSlice.reducer;
